import { Component, type ReactNode } from 'react';

/**
 * Top-level error boundary. Catches any uncaught React render error and
 * shows a tidy fallback UI instead of a blank screen. The fallback offers
 * a single action ("Reload"), since the underlying engine state is likely
 * compromised — a soft reset is the cleanest recovery path.
 *
 * Production-only failure logging: in dev we re-throw the error in the
 * console via componentDidCatch so the React red overlay still surfaces.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: unknown) {
    console.error('[CellSnake] uncaught render error:', error);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-bg-deep text-white p-6">
        <div className="hud-panel max-w-md w-full px-7 py-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-2">
            something broke
          </div>
          <div className="text-base mb-3">
            CellSnake hit an unexpected error.
          </div>
          <pre className="text-[10px] text-white/45 leading-snug whitespace-pre-wrap break-words mb-5 max-h-32 overflow-y-auto text-left bg-black/30 rounded p-2 border border-white/10">
            {this.state.error.message || String(this.state.error)}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-1.5 rounded text-[10px] uppercase tracking-[0.25em] bg-cyan-500/20 border border-cyan-300/40 text-cyan-100 hover:bg-cyan-500/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
